import {
  CorpJobberEmploymentDeductionsConfigDTO,
  WorkJurisdictionIdentifier,
} from '../orgViews/orgConstants';
import { isValidString } from './CommonUtilsMethods';

/**
 * Client mirror of server WorkJurisdictionTimezoneUtils.
 * Maps a work location's jurisdiction to the IANA zone used for shift wall-clock
 * interpretation and display (Pattern 1 worksite timezone).
 *
 * Money boundaries (pay periods, billing cutoffs, pay stubs) stay on agency Eastern
 * and must not use this helper.
 *
 * ## Phase B1 audit — shift-create epoch pass-through
 *
 * ShiftRequestCreationAPIService / ShiftRequestConverter persist client-supplied
 * startDateOfSRUnitEpochSeconds / endDateOfSRUnitEpochSeconds onto the unit as-is
 * (see ShiftRequestConverter mapping to startDateTimeEpochSeconds). The server does
 * not re-derive those epochs from an Eastern wall-clock.
 *
 * Related EST usage on create is presentation/matching only (e.g. startDateTime
 * string, startDay, startTime, workyWorkdayShift via TimeUtils EST helpers). That
 * does not change the stored UTC instant. Chunks B2/B3 fix client wall-clock ->
 * epoch conversion; B4 may revisit EST-derived display fields if BC day labels drift.
 */

/**
 * Fixed IANA wall-clock zones for shift create (Pattern 1 default + Pattern 2 override).
 * Enum values are the IANA ids passed to Intl / TimeUtils timeZone params.
 * UI labels lead with the named zone (Pacific / Eastern / …); IANA stays under the hood.
 */
enum ShiftWallClockTimezone {
  AMERICA_VANCOUVER = 'America/Vancouver',
  AMERICA_EDMONTON = 'America/Edmonton',
  AMERICA_DENVER = 'America/Denver',
  AMERICA_CHICAGO = 'America/Chicago',
  AMERICA_TORONTO = 'America/Toronto',
  AMERICA_HALIFAX = 'America/Halifax',
  AMERICA_ST_JOHNS = 'America/St_Johns',
  AMERICA_ANCHORAGE = 'America/Anchorage',
  PACIFIC_HONOLULU = 'Pacific/Honolulu',
  /**
   * Same Eastern offset family as Toronto; kept for parse / legacy callers.
   * Not shown in the create picker (use AMERICA_TORONTO for Eastern).
   */
  AMERICA_NEW_YORK = 'America/New_York',
}

const AMERICA_TORONTO_ZONE: ShiftWallClockTimezone = ShiftWallClockTimezone.AMERICA_TORONTO;
const AMERICA_VANCOUVER_ZONE: ShiftWallClockTimezone = ShiftWallClockTimezone.AMERICA_VANCOUVER;
const AMERICA_EDMONTON_ZONE: ShiftWallClockTimezone = ShiftWallClockTimezone.AMERICA_EDMONTON;
const AMERICA_NEW_YORK_ZONE: ShiftWallClockTimezone = ShiftWallClockTimezone.AMERICA_NEW_YORK;
const DEFAULT_SHIFT_WALL_CLOCK_ZONE: ShiftWallClockTimezone = ShiftWallClockTimezone.AMERICA_TORONTO;

/**
 * Major North America zones for Pattern 2 override.
 * Eastern and Pacific first (most common for Worky); then Mountain / Central / Atlantic.
 * Alaska / Hawaii / Newfoundland omitted for now; can be re-added later.
 */
const SHIFT_WALL_CLOCK_ZONE_OPTIONS: readonly ShiftWallClockTimezone[] = [
  ShiftWallClockTimezone.AMERICA_TORONTO,
  ShiftWallClockTimezone.AMERICA_VANCOUVER,
  ShiftWallClockTimezone.AMERICA_EDMONTON,
  ShiftWallClockTimezone.AMERICA_DENVER,
  ShiftWallClockTimezone.AMERICA_CHICAGO,
  ShiftWallClockTimezone.AMERICA_HALIFAX,
];

/**
 * Human-readable label for shift wall-clock timezone options and confirm summary.
 * Leads with the named zone; optional famous city in parentheses. IANA is not shown.
 */
const getShiftWallClockTimezoneLabel = (
  zone: ShiftWallClockTimezone | string
): string => {
  if (zone === ShiftWallClockTimezone.AMERICA_VANCOUVER) {
    return 'Pacific Time (Vancouver / Los Angeles)';
  }
  if (zone === ShiftWallClockTimezone.AMERICA_EDMONTON) {
    return 'Mountain Time (Edmonton)';
  }
  if (zone === ShiftWallClockTimezone.AMERICA_DENVER) {
    return 'Mountain Time (e.g. Denver)';
  }
  if (zone === ShiftWallClockTimezone.AMERICA_CHICAGO) {
    return 'Central Time (e.g. Chicago)';
  }
  if (
    zone === ShiftWallClockTimezone.AMERICA_TORONTO ||
    zone === ShiftWallClockTimezone.AMERICA_NEW_YORK
  ) {
    return 'Eastern Time (Toronto / New York)';
  }
  if (zone === ShiftWallClockTimezone.AMERICA_HALIFAX) {
    return 'Atlantic Time (e.g. Halifax)';
  }
  if (zone === ShiftWallClockTimezone.AMERICA_ST_JOHNS) {
    return "Newfoundland Time (e.g. St. John's)";
  }
  if (zone === ShiftWallClockTimezone.AMERICA_ANCHORAGE) {
    return 'Alaska Time (e.g. Anchorage)';
  }
  if (zone === ShiftWallClockTimezone.PACIFIC_HONOLULU) {
    return 'Hawaii Time (e.g. Honolulu)';
  }
  return zone;
};

/**
 * Coerces an unknown IANA / form string into a ShiftWallClockTimezone.
 * Unknown or missing values default to America/Toronto.
 */
const parseShiftWallClockTimezone = (
  input: string | null | undefined
): ShiftWallClockTimezone => {
  if (input === ShiftWallClockTimezone.AMERICA_VANCOUVER) {
    return ShiftWallClockTimezone.AMERICA_VANCOUVER;
  }
  if (input === ShiftWallClockTimezone.AMERICA_EDMONTON) {
    return ShiftWallClockTimezone.AMERICA_EDMONTON;
  }
  if (input === ShiftWallClockTimezone.AMERICA_DENVER) {
    return ShiftWallClockTimezone.AMERICA_DENVER;
  }
  if (input === ShiftWallClockTimezone.AMERICA_CHICAGO) {
    return ShiftWallClockTimezone.AMERICA_CHICAGO;
  }
  if (input === ShiftWallClockTimezone.AMERICA_TORONTO) {
    return ShiftWallClockTimezone.AMERICA_TORONTO;
  }
  if (input === ShiftWallClockTimezone.AMERICA_HALIFAX) {
    return ShiftWallClockTimezone.AMERICA_HALIFAX;
  }
  if (input === ShiftWallClockTimezone.AMERICA_ST_JOHNS) {
    return ShiftWallClockTimezone.AMERICA_ST_JOHNS;
  }
  if (input === ShiftWallClockTimezone.AMERICA_ANCHORAGE) {
    return ShiftWallClockTimezone.AMERICA_ANCHORAGE;
  }
  if (input === ShiftWallClockTimezone.PACIFIC_HONOLULU) {
    return ShiftWallClockTimezone.PACIFIC_HONOLULU;
  }
  if (input === ShiftWallClockTimezone.AMERICA_NEW_YORK) {
    return ShiftWallClockTimezone.AMERICA_TORONTO;
  }
  return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
};

const getShiftZoneIdFromJurisdiction = (
  workJurisdictionIdentifier: WorkJurisdictionIdentifier | string | null | undefined
): ShiftWallClockTimezone => {
  if (!isValidString(workJurisdictionIdentifier)) {
    return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
  }

  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.BRITISH_COLUMBIA) {
    return ShiftWallClockTimezone.AMERICA_VANCOUVER;
  }

  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.ALBERTA) {
    return ShiftWallClockTimezone.AMERICA_EDMONTON;
  }

  return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
};

/**
 * Resolves the default shift wall-clock zone for a selected org location.
 * Defaults to America/Toronto when the location or jurisdiction is missing.
 * Falls back to province when jurisdiction is absent (e.g. address-only payloads).
 */
const getShiftZoneIdFromProvince = (
  province: string | null | undefined
): ShiftWallClockTimezone => {
  if (!isValidString(province)) {
    return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
  }
  const normalizedProvince: string = province.trim().toUpperCase();
  if (
    normalizedProvince === 'BC' ||
    normalizedProvince === 'BRITISH COLUMBIA' ||
    normalizedProvince === 'BRITISH_COLUMBIA'
  ) {
    return ShiftWallClockTimezone.AMERICA_VANCOUVER;
  }
  if (
    normalizedProvince === 'AB' ||
    normalizedProvince === 'ALBERTA'
  ) {
    return ShiftWallClockTimezone.AMERICA_EDMONTON;
  }
  return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
};

const resolveShiftDisplayTimeZone = (input: {
  readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier | string | null;
  readonly province?: string | null;
}): ShiftWallClockTimezone => {
  if (isValidString(input.workJurisdictionIdentifier)) {
    return getShiftZoneIdFromJurisdiction(input.workJurisdictionIdentifier);
  }
  return getShiftZoneIdFromProvince(input.province);
};

/**
 * Resolves the IANA zone for paystub / shift-payment UI wall clocks.
 * Prefer pay-cycle jurisdiction when present; otherwise facility jurisdiction / province.
 */
const resolvePayStubShiftDisplayTimeZone = (input: {
  readonly payCycleWorkJurisdictionIdentifier?: WorkJurisdictionIdentifier | string | null;
  readonly orgLocation?: {
    readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier | string | null;
    readonly locationAddress?: { readonly province?: string | null } | null;
  } | null;
}): ShiftWallClockTimezone => {
  if (isValidString(input.payCycleWorkJurisdictionIdentifier)) {
    return getShiftZoneIdFromJurisdiction(input.payCycleWorkJurisdictionIdentifier);
  }
  return getShiftZoneIdFromOrgLocation(input.orgLocation);
};

const getShiftZoneIdFromOrgLocation = (input: {
  readonly workJurisdictionIdentifier?: WorkJurisdictionIdentifier | string | null;
  readonly locationAddress?: { readonly province?: string | null } | null;
} | null | undefined): ShiftWallClockTimezone => {
  if (!input) {
    return DEFAULT_SHIFT_WALL_CLOCK_ZONE;
  }
  return resolveShiftDisplayTimeZone({
    workJurisdictionIdentifier: input.workJurisdictionIdentifier,
    province: input.locationAddress?.province,
  });
};

/**
 * True when the selected shift wall-clock zone differs from the location's
 * jurisdiction default (Pattern 1).
 */
const hasShiftTimezoneMismatchWithLocation = (input: {
  readonly shiftWallClockTimezone: ShiftWallClockTimezone;
  readonly orgLocation:
    | {
        readonly workJurisdictionIdentifier?: string | null;
      }
    | null
    | undefined;
}): boolean => {
  const locationZone: ShiftWallClockTimezone = getShiftZoneIdFromOrgLocation(
    input.orgLocation
  );
  return input.shiftWallClockTimezone !== locationZone;
};

/**
 * Short timezone abbreviation for display (e.g. EDT, EST, PDT) for an instant in a zone.
 * Falls back to the IANA zone id when the runtime cannot resolve a short name.
 */
const getShortTimezoneAbbreviation = (
  instant: Date,
  timeZone: string
): string => {
  if (Number.isNaN(instant.getTime()) || !isValidString(timeZone)) {
    return '';
  }
  try {
    const parts: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(instant);
    const tzPart: Intl.DateTimeFormatPart | undefined = parts.find(
      (part: Intl.DateTimeFormatPart) => part.type === 'timeZoneName'
    );
    return isValidString(tzPart?.value) ? (tzPart?.value as string) : timeZone;
  } catch {
    return timeZone;
  }
};

/**
 * Formats a Date's calendar date in the given IANA zone (locale default style).
 */
const formatDateInTimeZone = (instant: Date, timeZone: string): string => {
  if (Number.isNaN(instant.getTime())) {
    return '';
  }
  return instant.toLocaleDateString(undefined, { timeZone });
};

/**
 * Formats a Date's clock time in the given IANA zone (hour + minute, 12-hour).
 */
const formatTimeInTimeZone = (instant: Date, timeZone: string): string => {
  if (Number.isNaN(instant.getTime())) {
    return '';
  }
  return instant.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
};

/**
 * Formats a clock time range in a zone with a short timezone abbreviation
 * (e.g. "09:00 AM - 05:00 PM PDT").
 */
const formatTimeRangeInTimeZoneWithAbbreviation = (
  startInstant: Date,
  endInstant: Date,
  timeZone: string
): string => {
  const startLabel: string = formatTimeInTimeZone(startInstant, timeZone);
  const endLabel: string = formatTimeInTimeZone(endInstant, timeZone);
  const abbr: string = getShortTimezoneAbbreviation(startInstant, timeZone);
  if (isValidString(abbr)) {
    return `${startLabel} - ${endLabel} ${abbr}`;
  }
  return `${startLabel} - ${endLabel}`;
};

/**
 * Formats a single clock time in a zone with a short timezone abbreviation
 * (e.g. "09:00 AM PDT").
 */
const formatTimeInTimeZoneWithAbbreviation = (
  instant: Date,
  timeZone: string
): string => {
  const timeLabel: string = formatTimeInTimeZone(instant, timeZone);
  const abbr: string = getShortTimezoneAbbreviation(instant, timeZone);
  if (isValidString(abbr)) {
    return `${timeLabel} ${abbr}`;
  }
  return timeLabel;
};

/**
 * Formats a work jurisdiction for client-facing labels.
 */
const formatWorkJurisdictionLabel = (
  workJurisdictionIdentifier: WorkJurisdictionIdentifier | string
): string => {
  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.BRITISH_COLUMBIA) {
    return 'British Columbia';
  }
  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.ALBERTA) {
    return 'Alberta';
  }
  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.ONTARIO) {
    return 'Ontario';
  }
  if (workJurisdictionIdentifier === WorkJurisdictionIdentifier.CANADA) {
    return 'Canada';
  }
  return String(workJurisdictionIdentifier);
};

/**
 * Lists jurisdiction keys present on agency workyPayCorpConfig (Map or plain object).
 * Includes British Columbia and Alberta when configured.
 */
const listConfiguredWorkJurisdictionIdentifiers = (
  workyPayCorpConfig:
    | Map<string, unknown>
    | Record<string, unknown>
    | null
    | undefined
): readonly WorkJurisdictionIdentifier[] => {
  if (!workyPayCorpConfig) {
    return [];
  }

  const keys: WorkJurisdictionIdentifier[] = [];
  const appendIfKnown = (key: string): void => {
    if (
      key === WorkJurisdictionIdentifier.ONTARIO
      || key === WorkJurisdictionIdentifier.BRITISH_COLUMBIA
      || key === WorkJurisdictionIdentifier.ALBERTA
      || key === WorkJurisdictionIdentifier.CANADA
    ) {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    }
  };

  if (workyPayCorpConfig instanceof Map) {
    workyPayCorpConfig.forEach((_value, key) => {
      appendIfKnown(String(key));
    });
  } else if (typeof workyPayCorpConfig === 'object') {
    Object.keys(workyPayCorpConfig).forEach((key: string) => {
      appendIfKnown(key);
    });
  }

  return keys;
};

/**
 * Jurisdiction choices for inviting workers: configured pay-config keys, always including
 * Ontario, British Columbia, and Alberta so each can be selected for paystub employment config.
 */
const listWorkerInviteWorkJurisdictionIdentifiers = (
  workyPayCorpConfig:
    | Map<string, unknown>
    | Record<string, unknown>
    | null
    | undefined
): readonly WorkJurisdictionIdentifier[] => {
  const configured: readonly WorkJurisdictionIdentifier[] =
    listConfiguredWorkJurisdictionIdentifiers(workyPayCorpConfig);
  const result: WorkJurisdictionIdentifier[] = [...configured];

  if (!result.includes(WorkJurisdictionIdentifier.ONTARIO)) {
    result.push(WorkJurisdictionIdentifier.ONTARIO);
  }
  if (!result.includes(WorkJurisdictionIdentifier.BRITISH_COLUMBIA)) {
    result.push(WorkJurisdictionIdentifier.BRITISH_COLUMBIA);
  }
  if (!result.includes(WorkJurisdictionIdentifier.ALBERTA)) {
    result.push(WorkJurisdictionIdentifier.ALBERTA);
  }

  return result;
};

type WorkyPayCorpConfigLike = {
  readonly corpJobberEmploymentDeductionsConfigs?: readonly CorpJobberEmploymentDeductionsConfigDTO[];
};

/**
 * Employment deduction configs for the invite form.
 * Prefers the selected jurisdiction; if that jurisdiction has no templates yet (e.g. BC
 * before corp config is fully set up), falls back to Ontario then any other configured list.
 */
const resolveInviteEmploymentDeductionConfigs = (
  workyPayCorpConfig: Record<string, WorkyPayCorpConfigLike> | null | undefined,
  selectedJurisdiction: string
): readonly CorpJobberEmploymentDeductionsConfigDTO[] => {
  if (!workyPayCorpConfig || !isValidString(selectedJurisdiction)) {
    return [];
  }

  const forSelected: readonly CorpJobberEmploymentDeductionsConfigDTO[] | undefined =
    workyPayCorpConfig[selectedJurisdiction]?.corpJobberEmploymentDeductionsConfigs;
  if (forSelected && forSelected.length > 0) {
    return forSelected;
  }

  const forOntario: readonly CorpJobberEmploymentDeductionsConfigDTO[] | undefined =
    workyPayCorpConfig[WorkJurisdictionIdentifier.ONTARIO]?.corpJobberEmploymentDeductionsConfigs;
  if (forOntario && forOntario.length > 0) {
    return forOntario;
  }

  const fallback: CorpJobberEmploymentDeductionsConfigDTO[] = [];
  Object.values(workyPayCorpConfig).forEach((config: WorkyPayCorpConfigLike) => {
    const configs: readonly CorpJobberEmploymentDeductionsConfigDTO[] | undefined =
      config?.corpJobberEmploymentDeductionsConfigs;
    if (configs && configs.length > 0) {
      configs.forEach((employmentConfig: CorpJobberEmploymentDeductionsConfigDTO) => {
        fallback.push(employmentConfig);
      });
    }
  });
  return fallback;
};

export {
  ShiftWallClockTimezone,
  AMERICA_TORONTO_ZONE,
  AMERICA_VANCOUVER_ZONE,
  AMERICA_EDMONTON_ZONE,
  AMERICA_NEW_YORK_ZONE,
  DEFAULT_SHIFT_WALL_CLOCK_ZONE,
  SHIFT_WALL_CLOCK_ZONE_OPTIONS,
  getShiftWallClockTimezoneLabel,
  parseShiftWallClockTimezone,
  hasShiftTimezoneMismatchWithLocation,
  getShiftZoneIdFromJurisdiction,
  getShiftZoneIdFromOrgLocation,
  resolveShiftDisplayTimeZone,
  resolvePayStubShiftDisplayTimeZone,
  getShortTimezoneAbbreviation,
  formatDateInTimeZone,
  formatTimeInTimeZone,
  formatTimeInTimeZoneWithAbbreviation,
  formatTimeRangeInTimeZoneWithAbbreviation,
  formatWorkJurisdictionLabel,
  listConfiguredWorkJurisdictionIdentifiers,
  listWorkerInviteWorkJurisdictionIdentifiers,
  resolveInviteEmploymentDeductionConfigs,
};
